<template>
    <div class="electricity-price-list">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">{{ $t("electricity_prices") }}</h6>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="addPrice">
                <i class="fa-solid fa-plus"></i> {{ $t("add_price") }}
            </button>
        </div>

        <div v-if="localPrices.length === 0" class="alert alert-info">
            {{ $t("no_electricity_prices") }}
        </div>

        <div class="table-responsive" v-else>
            <table class="table table-sm table-bordered">
                <thead class="table-light">
                    <tr>
                        <th style="width: 30%">{{ $t("price_per_kwh") }}</th>
                        <th style="width: 25%">{{ $t("valid_from") }}</th>
                        <th style="width: 25%">{{ $t("valid_to") }}</th>
                        <th style="width: 20%">{{ $t("actions") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(price, index) in localPrices" :key="index" :class="{ 'table-success': isCurrentPrice(price) }">
                        <td>
                            <div class="input-group input-group-sm">
                                <input
                                    type="number"
                                    class="form-control"
                                    v-model.number="price.price_per_kwh"
                                    step="0.0001"
                                    min="0"
                                    @change="emitChange"
                                >
                                <span class="input-group-text">EUR/kWh</span>
                            </div>
                        </td>
                        <td>
                            <input
                                type="date"
                                class="form-control form-control-sm"
                                v-model="price.valid_from"
                                @change="emitChange"
                            >
                        </td>
                        <td>
                            <input
                                type="date"
                                class="form-control form-control-sm"
                                v-model="price.valid_to"
                                :placeholder="$t('unlimited')"
                                @change="emitChange"
                            >
                            <small v-if="!price.valid_to" class="text-muted">{{ $t("unlimited") }}</small>
                        </td>
                        <td>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                @click="removePrice(index)"
                                :title="$t('delete')"
                            >
                                <i class="fa-solid fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="validationError" class="alert alert-danger mt-2">
            {{ validationError }}
        </div>

        <div v-if="currentPriceDisplay !== null" class="mt-2">
            <small class="text-muted">
                {{ $t("current_price") }}: <strong>{{ formatPrice(currentPriceDisplay) }} EUR/kWh</strong>
            </small>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ElectricityPriceList',
    props: {
        prices: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localPrices: [],
            validationError: null
        }
    },
    computed: {
        currentPriceDisplay() {
            const today = new Date().toISOString().split('T')[0]
            const currentPrice = this.localPrices.find(p => {
                const validFrom = p.valid_from
                const validTo = p.valid_to
                return validFrom <= today && (!validTo || validTo >= today)
            })
            return currentPrice ? currentPrice.price_per_kwh : null
        }
    },
    watch: {
        prices: {
            handler(newVal) {
                this.localPrices = JSON.parse(JSON.stringify(newVal || []))
                // Sortiere nach valid_from absteigend
                this.localPrices.sort((a, b) => {
                    if (!a.valid_from) return 1
                    if (!b.valid_from) return -1
                    return b.valid_from.localeCompare(a.valid_from)
                })
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        addPrice() {
            const today = new Date().toISOString().split('T')[0]
            this.localPrices.unshift({
                price_per_kwh: 0,
                valid_from: today,
                valid_to: null
            })
            this.emitChange()
        },
        removePrice(index) {
            this.localPrices.splice(index, 1)
            this.emitChange()
        },
        emitChange() {
            this.validate()
            this.$emit('update:prices', this.localPrices)
        },
        validate() {
            this.validationError = null

            // Prüfe auf überlappende Zeiträume
            for (let i = 0; i < this.localPrices.length; i++) {
                const p1 = this.localPrices[i]
                if (!p1.valid_from) {
                    this.validationError = this.$t("valid_from_required")
                    return false
                }

                for (let j = i + 1; j < this.localPrices.length; j++) {
                    const p2 = this.localPrices[j]
                    if (this.intervalsOverlap(p1, p2)) {
                        this.validationError = this.$t("overlapping_periods")
                        return false
                    }
                }
            }

            // Prüfe auf mehrere unbegrenzte Preise
            const unlimitedCount = this.localPrices.filter(p => !p.valid_to).length
            if (unlimitedCount > 1) {
                this.validationError = this.$t("multiple_unlimited_prices")
                return false
            }

            return true
        },
        intervalsOverlap(p1, p2) {
            const start1 = p1.valid_from
            const end1 = p1.valid_to || '9999-12-31'
            const start2 = p2.valid_from
            const end2 = p2.valid_to || '9999-12-31'

            return start1 <= end2 && start2 <= end1
        },
        isCurrentPrice(price) {
            const today = new Date().toISOString().split('T')[0]
            const validFrom = price.valid_from
            const validTo = price.valid_to
            return validFrom <= today && (!validTo || validTo >= today)
        },
        formatPrice(value) {
            return new Intl.NumberFormat('de-DE', {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            }).format(value)
        }
    }
}
</script>

<style scoped>
.electricity-price-list {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
}
</style>
